/** Shared compact rank, drag handle and Move menu for Table and Pitch. */
import { watchlistMoveMenuItems } from "@/features/scouting/utils/watchlistMoveMenuItems";
import { useSortable } from "@dnd-kit/sortable";
import { useDndContext } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Platform } from "react-native";

import type { Pressable } from "@/components/atoms/Pressable";

import WatchlistOrderControlsView from "./WatchlistOrderControlsView";

/** Destinations exposed by each row/card Move menu. */
export type WatchlistMoveDestination = "top" | "up" | "down" | "bottom";

/** Props shared by Table rows and Pitch membership rows. */
export interface WatchlistOrderControlsProps {
  /** Fixed content track inside the desktop table's 48px row. */
  readonly tableRow?: boolean;
  readonly compact?: boolean;
  readonly playerId: string;
  readonly dndId?: string;
  readonly groupIds?: readonly string[];
  readonly playerName: string;
  readonly rank: number;
  readonly canEdit: boolean;
  readonly paused?: boolean;
  readonly isFirst: boolean;
  readonly isLast: boolean;
  readonly onMove: (destination: WatchlistMoveDestination) => void;
}

/** Prepared props consumed by the presentation component. */
export interface WatchlistOrderControlsViewProps {
  readonly tableRow?: boolean;
  readonly compact?: boolean;
  readonly playerId: string;
  readonly playerName: string;
  readonly rank: number;
  readonly canEdit: boolean;
  readonly paused: boolean;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onToggle: () => void;
  readonly items: React.ComponentProps<
    typeof import("@/components/molecules/DesktopDropdownMenu").default
  >["items"];
  readonly nodeRef: (node: HTMLElement | null) => void;
  readonly activatorRef: (node: HTMLElement | null) => void;
  readonly dragBindings: Partial<React.ComponentProps<typeof Pressable>>;
}

/**
 * Prepares dnd-kit bindings and stable movement handlers.
 * @param root0 - Component props.
 * @param root0.compact - Fit the existing grip and menu within a compact pitch row.
 * @param root0.tableRow - Bind the full desktop table row for movement.
 * @param root0.playerId - Sortable player identifier.
 * @param root0.groupIds - Restricts a grouped row to its position peers.
 * @param root0.dndId - Optional collision-free drag identity for this surface.
 * @param root0.playerName - Player name for accessible labels.
 * @param root0.rank - Current global rank.
 * @param root0.canEdit - Whether movement is authorized.
 * @param root0.paused - Whether explicit sorting pauses movement.
 * @param root0.isFirst - Whether the player is first in the visible order.
 * @param root0.isLast - Whether the player is last in the visible order.
 * @param root0.onMove - Applies a menu movement destination.
 */
const WatchlistOrderControlsContainer = ({
  tableRow,
  compact,
  playerId,
  dndId,
  groupIds,
  playerName,
  rank,
  canEdit,
  paused = false,
  isFirst,
  isLast,
  onMove,
}: WatchlistOrderControlsProps) => {
  const node = useRef<HTMLElement | null>(null);
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({
    id: dndId ?? playerId,
    disabled: !canEdit || paused,
    data: { elementRef: node, groupIds },
  });
  const { activatorEvent, active } = useDndContext();
  const dragBindings = useMemo(
    () =>
      ({
        ...attributes,
        ...listeners,
      }) as Partial<React.ComponentProps<typeof Pressable>>,
    [attributes, listeners]
  );
  const [isOpen, setIsOpen] = useState(false);
  const toggle = useCallback(() => setIsOpen(open => !open), []);
  const close = useCallback(() => setIsOpen(false), []);
  const items = useMemo(
    () => watchlistMoveMenuItems(onMove, isFirst, isLast, paused),
    [isFirst, isLast, onMove, paused]
  );
  const connectNode = useCallback(
    (nextNode: HTMLElement | null) => {
      const sortableNode =
        tableRow && Platform.OS === "web"
          ? (nextNode?.closest<HTMLElement>("tr") ?? nextNode)
          : nextNode;
      node.current = sortableNode;
      setNodeRef(sortableNode);
    },
    [setNodeRef, tableRow]
  );
  useEffect(() => {
    if (Platform.OS !== "web") return;
    const element = node.current;
    if (!element) return;
    const isKeyboardDrag = activatorEvent?.type === "keydown";
    element.style.setProperty(
      "transform",
      (tableRow && !isDragging) || (!tableRow && isKeyboardDrag)
        ? (CSS.Transform.toString(transform) ?? "")
        : ""
    );
    element.style.setProperty(
      "transition",
      tableRow || isKeyboardDrag ? (transition ?? "") : ""
    );
    if (tableRow) {
      element.classList.toggle("opacity-30", isDragging);
      const target = !!active && !isDragging && isOver;
      element.classList.toggle("border-t-2", target);
      element.classList.toggle("border-accent-primary", target);
      element.setAttribute("data-reorder-target", String(target));
    }
  }, [
    activatorEvent,
    transform,
    transition,
    tableRow,
    isDragging,
    active,
    isOver,
  ]);
  useEffect(() => {
    if (!paused) return undefined;
    const timer = setTimeout(close, 0);
    return () => clearTimeout(timer);
  }, [close, paused]);

  return (
    <WatchlistOrderControlsView
      tableRow={tableRow}
      compact={compact}
      playerId={playerId}
      playerName={playerName}
      rank={rank}
      canEdit={canEdit}
      paused={paused}
      isOpen={isOpen}
      onClose={close}
      onToggle={toggle}
      items={items}
      nodeRef={connectNode}
      activatorRef={setActivatorNodeRef}
      dragBindings={dragBindings}
    />
  );
};

WatchlistOrderControlsContainer.displayName = "WatchlistOrderControlsContainer";
export default memo(WatchlistOrderControlsContainer);
